"use client";

import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Flex,
  Icon,
  IconButton,
  Image,
  Spacing,
  Text,
  type UIAction,
  useActions,
  useHover,
  useThemeSlot,
} from "@huub-web/ui";
import type { ThemeSlotPropsMap } from "@huub-web/ui/contexts/ThemeSlotContext";
import { formatNumberAsMoney } from "@huub-web/utils";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";
import { theme } from "@huub-web/ui/theme/css";

type DefaultHouseAnalysisCardProps = ThemeSlotPropsMap["hubHouseAnalysisCard"];

const DefaultHouseAnalysisCard: FC<DefaultHouseAnalysisCardProps> = ({
  action,
  disabled,
  propertyImage,
  propertyPrice,
  propertyAddress,
  propertyAction,
}) => {
  const { t } = useTranslation();
  const { handleAction } = useActions();

  const handleCtaClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  const handlePropertyClick = () => {
    if (propertyAction) {
      handleAction(propertyAction);
    }
  };

  const isPropertyActionDisabled = disabled || !propertyAction;
  const { hasHover, handleEnter, handleLeave } = useHover({
    disabled: isPropertyActionDisabled,
  });

  const hasPropertyData =
    !!propertyImage || propertyPrice !== undefined || !!propertyAddress;

  return (
    <Card border="default" padding="l">
      {hasPropertyData && (
        <Flex
          as="button"
          type="button"
          direction="column"
          gap="s"
          disabled={isPropertyActionDisabled}
          onClick={handlePropertyClick}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          style={{ cursor: isPropertyActionDisabled ? "default" : "pointer" }}
        >
          {propertyImage && (
            <Image
              src={propertyImage}
              alt={propertyAddress ?? t("houseAnalysisHubCard.alt")}
              width="100%"
              fit="cover"
              style={{
                aspectRatio: "16 / 9",
                borderRadius: theme.borderRadiuses.onXS,
              }}
            />
          )}
          {(propertyPrice !== undefined || propertyAddress) && (
            <Flex justify="space-between" align="center" gap="s">
              <Flex direction="column" align="flex-start" gap="xxs">
                {propertyPrice !== undefined && (
                  <Text variant="bodyL" bold>
                    {formatNumberAsMoney(propertyPrice)}
                  </Text>
                )}
                {propertyAddress && (
                  <Text variant="bodyM">{propertyAddress}</Text>
                )}
              </Flex>
              <IconButton
                as="div"
                icon="chevronRight"
                variant="tertiary"
                size="s"
                rounded
                active={hasHover}
              />
            </Flex>
          )}
        </Flex>
      )}
      <Spacing grow />
      <Button
        variant="tertiary"
        size="s"
        trailing={<Icon icon="chevronRight" />}
        width="stretch"
        disabled={disabled}
        onClick={handleCtaClick}
      >
        {t("houseAnalysisHubCard.cta")}
      </Button>
    </Card>
  );
};

export type HouseAnalysisHubCardTemplateProps = {
  /**
   * Optional override for the card title; falls back to i18n key
   */
  title?: string;
  /**
   * Optional override for the card description; falls back to i18n key
   */
  description?: string;
  /**
   * The action to perform when the CTA is clicked
   */
  action?: UIAction;
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
  /**
   * URL of the property image to display
   */
  propertyImage?: string;
  /**
   * Asking price of the property in euros
   */
  propertyPrice?: number;
  /**
   * Address of the property
   */
  propertyAddress?: string;
  /**
   * The action to perform when the property section is clicked
   */
  propertyAction?: UIAction;
};

export const HouseAnalysisHubCardTemplate: FC<
  HouseAnalysisHubCardTemplateProps
> = ({
  title,
  description,
  action,
  disabled,
  propertyImage,
  propertyPrice,
  propertyAddress,
  propertyAction,
}) => {
  const { t } = useTranslation();
  const { lockPrompts, lockUI } = useTemplates();

  const SlotComponent = useThemeSlot({
    key: "hubHouseAnalysisCard",
    defaultComponent: DefaultHouseAnalysisCard,
  });

  const resolvedTitle = title ?? t("houseAnalysisHubCard.title");
  const resolvedDescription =
    description ?? t("houseAnalysisHubCard.description");

  const contentItem = useMemo(
    () => ({
      type: "card" as const,
      label: resolvedTitle,
      description: resolvedDescription,
      meta: {
        ...(propertyPrice != null ? { propertyPrice } : {}),
        ...(propertyAddress ? { propertyAddress } : {}),
      },
      ...(action ? { action } : {}),
    }),
    [
      resolvedTitle,
      resolvedDescription,
      action,
      propertyPrice,
      propertyAddress,
    ],
  );
  usePageContent("hubcard-houseAnalysis", contentItem);

  const isDisabled = (() => {
    if (action?.type === "prompt") {
      return lockPrompts;
    }
    return lockUI || disabled;
  })();

  return (
    <SlotComponent
      title={resolvedTitle}
      description={resolvedDescription}
      action={action}
      disabled={isDisabled}
      propertyImage={propertyImage}
      propertyPrice={propertyPrice}
      propertyAddress={propertyAddress}
      propertyAction={propertyAction}
    />
  );
};
